import classnames from 'classnames';
import React, { Component } from 'react';
import { IconHelpOutline } from '@kmx/legos-react-icons';
import { FormContext, Icon, Tooltip } from '..';
import './TextField.scss';

class TextField extends Component {
    constructor(props) {
        super(props);
        const maskedText = props.value ? new Array(this.props.value.length).fill('•').join('') : '';
        const initValue = props.value ? (props.format ? props.format(props.value) : props.value) : '';

        this.state = {
            errorText: null,
            hasError: false,
            isFocused: false,
            maskedText: maskedText,
            value: initValue,
        };

        this.id = this.props.id || this.props.name.replace('s+', '-');
        this.name = this.props.name || this.props.id;

        // Methods
        // this.determineType = this.determineType.bind(this);
        this.clearValue = this.clearValue.bind(this);
        this.determineValue = this.determineValue.bind(this);
        this.templateText = this.templateText.bind(this);
        this.validate = this.validate.bind(this);
    }

    clearValue() {
        this.setState({ value: '' });
    }

    componentDidMount() {
        if (this.context) {
            this.context.addInputField(this);
        }

        this.validate(false);
    }

    componentWillUnmount() {
        if (this.context) {
            this.context.removeInputField(this);
        }
    }

    componentDidUpdate(prevProps) {
        const { format, value } = this.props;

        if (!!value && value !== prevProps.value && value !== this.state.value) {
            const nextValue = format ? format(value) : value;
            this.setState({ value: nextValue }, () => this.validate(true));
        }
    }

    determineValue() {
        const { format, mask } = this.props;
        const { isFocused, maskedText, value } = this.state;
        let nextValue = value;

        if (!isFocused && mask) {
            nextValue = maskedText;
        } else if (!!format && value) {
            nextValue = format(nextValue);
        }

        return nextValue;
    }

    onBlurHandler(event) {
        this.props.onBlur && this.props.onBlur(event.target.value, event);
        this.setState({ isFocused: false }, () => this.validate(true));
    }

    onChangeHandler(event) {
        event.persist();
        const value = this.props.format ? this.props.format(event.target.value) : event.target.value;
        const selectionStart = this.props.format
            ? this.props.format(event.target.value.substr(0, event.target.selectionStart)).length
            : event.target.selectionStart;

        this.props.onChange && this.props.onChange(value, event);
        const nextMaskedText = new Array(event.target.value.length).fill('•').join('') || '';

        this.setState(
            {
                maskedText: nextMaskedText,
                value,
            },
            () => {
                event.target.setSelectionRange(selectionStart, selectionStart);
                this.validate(false);
            }
        );
    }

    onFocusHandler(e) {
        this.props.onFocus && this.props.onFocus(e);
        this.setState({ isFocused: true });
    }

    templateText() {
        const { value } = this.state;
        const { template } = this.props;

        if (!value) {
            return template;
        }

        const len = value.length;
        const pre = new Array(len + 1).join(' ');
        const str = pre + template.slice(len);

        return str;
    }

    validate(showError) {
        const { required, validation } = this.props;
        const { value } = this.state;

        showError = showError === undefined ? true : showError;
        const isEmpty = value === null || value.trim() === '';
        let errorText = null;
        let valid = true;

        if (required && isEmpty) {
            valid = false;
        } else if (validation && !isEmpty) {
            const results = validation(value);

            if (Array.isArray(results)) {
                valid = results[0];
                errorText = results[1];
            } else {
                valid = results;
            }
        }

        if (showError) {
            this.setState({ errorText, hasError: !valid });
        }

        // onValidate && onValidate(this.name, valid);
        this.context && this.context.onValidate(this.name, valid);
    }

    render() {
        const {
            align,
            className,
            disabled,
            helperText,
            helperTextPersistent,
            icon,
            label,
            maxLength,
            minLength,
            inputMode,
            tooltipContent,
            tooltipcontainer,
            placeholder,
            required,
            showRequired,
            template,
            templatePersistent,
            type,
        } = this.props;
        const { hasError, isFocused } = this.state;
        const value = this.determineValue();
        const errorText = this.state.errorText === null ? this.props.errorText : this.state.errorText;

        return (
            <div
                className={classnames([
                    'kmx-text-field',
                    { 'kmx-text-field--disabled': disabled },
                    { 'kmx-text-field--icon': icon },
                    { [`kmx-text-field--${this.name}`]: this.name },
                    { 'kmx-text-field--template': template },
                    { 'kmx-text-field--tooltip': tooltipContent },
                    className,
                ])}>
                <div className="kmx-text-field__wrapper">
                    <input
                        className={classnames([
                            'kmx-text-field__input',
                            { 'text-right': align === 'right' ? true : false },
                        ])}
                        disabled={disabled}
                        id={this.id}
                        name={this.name}
                        onBlur={this.onBlurHandler.bind(this)}
                        onChange={this.onChangeHandler.bind(this)}
                        onFocus={this.onFocusHandler.bind(this)}
                        placeholder={placeholder || ' '}
                        type={type}
                        value={value}
                        maxLength={maxLength}
                        minLength={minLength}
                        inputMode={inputMode}
                    />
                    {icon ? (
                        <span className="kmx-text-field__icon">
                            <Icon fill="#6D747A" icon={icon} height={18} width={18} />
                        </span>
                    ) : null}
                    <label
                        className={classnames([
                            'kmx-text-field__label',
                            { 'kmx-text-field__label--error': hasError },
                            { 'kmx-text-field__label--float-above': isFocused || placeholder },
                            { 'kmx-text-field__label--focus': isFocused },
                            { 'kmx-text-field__label--required': required && showRequired },
                            { 'kmx-text-field__label--float-above': value },
                        ])}
                        htmlFor={this.id}>
                        {label}
                    </label>

                    {tooltipContent ? (
                        <Tooltip
                            className="kmx-text-field__tooltip"
                            content={tooltipContent}
                            tooltipcontainer={tooltipcontainer}>
                            <IconHelpOutline width={20} height={20} />
                        </Tooltip>
                    ) : null}

                    {(template && isFocused) || (template && templatePersistent) ? (
                        <div
                            className={classnames([
                                'kmx-text-field__template',
                                { 'kmx-text-field__template--persistent': templatePersistent },
                            ])}>
                            {this.templateText()}
                        </div>
                    ) : null}

                    <div
                        className={classnames([
                            'kmx-text-field__border',
                            { 'kmx-text-field__border--error': hasError },
                            { 'kmx-text-field__border--focus': isFocused },
                        ])}></div>
                </div>

                {errorText ? (
                    <div
                        className={classnames([
                            'kmx-text-field__error-text',
                            { 'kmx-text-field__error-text--active': hasError },
                        ])}>
                        {errorText}
                    </div>
                ) : null}

                {helperText ? (
                    <div
                        className={classnames([
                            'kmx-text-field__helper-text',
                            { 'kmx-text-field__helper-text--hide': hasError },
                            { 'kmx-text-field__helper-text--persistent': helperTextPersistent },
                        ])}>
                        {helperText}
                    </div>
                ) : null}
            </div>
        );
    }
}

TextField.contextType = FormContext;

export { TextField as default };
